import { useEffect } from 'react';

import { FLOATING_TABLE_HEADER_OFFSET, TABLE_MOBILE_BREAKPOINT, TABLE_NAVIGATION_HEIGHT } from './constants';

// This file keeps the column headings visible when the user scrolls down the page.
// It also connects the scrollbar above the table to the table's sideways scrolling.
// The table is the source of the scroll position: moving either scrollbar scrolls
// the table, and we then move the headings and top scrollbar thumb to match it.

// Keep the draggable part large enough to grab, unless the whole track is smaller.
const MIN_THUMB_WIDTH = 40;
// Start scrolling within 48 pixels of either visible edge while dragging a column.
const COLUMN_DRAG_EDGE_WIDTH = 48;
// Scroll by up to 12 pixels per animation frame, faster as the pointer approaches the edge.
const COLUMN_DRAG_SCROLL_STEP = 12;

// The "track" is the full scrollbar area. The "thumb" is the part the user drags.
// This function only changes how the thumb looks. It does not scroll the table.
const updateScrollThumb = (tableScrollElement, track, thumb) => {
	if (!track || !thumb || !tableScrollElement) return;

	// clientWidth is the visible width; scrollWidth includes the columns hidden offscreen.
	// For example, if half the table fits onscreen, the thumb takes up half the track.
	// When there is no content width, use 1 to avoid dividing by zero.
	const ratio = tableScrollElement.scrollWidth ? tableScrollElement.clientWidth / tableScrollElement.scrollWidth : 1;
	// Keep the thumb at least 40 pixels wide, but never wider than its track.
	const thumbWidth = Math.min(track.clientWidth, Math.max(track.clientWidth * ratio, MIN_THUMB_WIDTH));
	// The thumb must stop before its right edge goes past the end of the track.
	const maxThumbLeft = track.clientWidth - thumbWidth;
	// The table can only scroll through the width that does not fit onscreen.
	const scrollRange = Math.max(0, tableScrollElement.scrollWidth - tableScrollElement.clientWidth);
	// Convert the current scroll position to a number from 0 (start) to 1 (end).
	// If everything fits, there is nowhere to scroll, so use 0.
	const scrollRatio = scrollRange ? Math.max(0, Math.min(tableScrollElement.scrollLeft / scrollRange, 1)) : 0;

	thumb.style.width = `${thumbWidth}px`;
	// translateX moves the thumb sideways by the calculated number of pixels.
	thumb.style.transform = `translateX(${scrollRatio * maxThumbLeft}px)`;
};

// Connect mouse, touch and pen actions on the top scrollbar to the actual table.
// These handlers change the table's scrollLeft, which is how far it is scrolled sideways.
// The table's scroll event then asks the hook below to update the header and thumb.
const attachScrollbarControls = (tableScrollElement, track, thumb) => {
	let isDragging = false;
	let dragStartX = 0;
	let dragStartScrollLeft = 0;

	const onThumbPointerDown = event => {
		// Ignore non-primary buttons, such as a right click, and tables that already fit.
		if (event.button !== 0 || tableScrollElement.scrollWidth <= tableScrollElement.clientWidth) return;
		// Remember where the pointer and table started so each move uses the same starting point.
		isDragging = true;
		dragStartX = event.clientX;
		dragStartScrollLeft = tableScrollElement.scrollLeft;
		// Pointer capture keeps sending this drag's events to the thumb even if the
		// pointer leaves it. Prevent the normal action so dragging does not select text.
		thumb.setPointerCapture(event.pointerId);
		event.preventDefault();
	};

	const onThumbPointerMove = event => {
		if (!isDragging) return;
		const maxThumbLeft = track.clientWidth - thumb.offsetWidth;
		const maxScrollLeft = tableScrollElement.scrollWidth - tableScrollElement.clientWidth;
		if (maxThumbLeft <= 0 || maxScrollLeft <= 0) return;
		// The thumb and table travel different distances. If the thumb can move 300 pixels
		// and the table can scroll 600 pixels, each pixel of dragging scrolls the table by 2.
		const scrollRatio = maxScrollLeft / maxThumbLeft;
		const nextScrollLeft = dragStartScrollLeft + (event.clientX - dragStartX) * scrollRatio;

		// Do not let the calculated position go before the start or past the end of the table.
		tableScrollElement.scrollLeft = Math.max(0, Math.min(nextScrollLeft, maxScrollLeft));
	};

	const onThumbPointerUp = event => {
		// Stop dragging both when the user releases the pointer and when the browser cancels it.
		// Capture may already be gone, so only release it if the thumb still has it.
		isDragging = false;
		if (thumb.hasPointerCapture(event.pointerId)) thumb.releasePointerCapture(event.pointerId);
	};

	const onTrackClick = event => {
		if (event.target === thumb || !track.clientWidth) return;

		// Find where the user clicked within the track, not within the whole page.
		// A click halfway along the track jumps halfway through the table's scroll range.
		const trackRect = track.getBoundingClientRect();
		const ratio = (event.clientX - trackRect.left) / track.clientWidth;
		tableScrollElement.scrollLeft = ratio * (tableScrollElement.scrollWidth - tableScrollElement.clientWidth);
	};

	thumb.addEventListener('pointerdown', onThumbPointerDown);
	thumb.addEventListener('pointermove', onThumbPointerMove);
	thumb.addEventListener('pointerup', onThumbPointerUp);
	thumb.addEventListener('pointercancel', onThumbPointerUp);
	thumb.addEventListener('lostpointercapture', onThumbPointerUp);
	track.addEventListener('click', onTrackClick);

	// Give the hook a function it can call to remove exactly the handlers we added.
	return () => {
		thumb.removeEventListener('pointerdown', onThumbPointerDown);
		thumb.removeEventListener('pointermove', onThumbPointerMove);
		thumb.removeEventListener('pointerup', onThumbPointerUp);
		thumb.removeEventListener('pointercancel', onThumbPointerUp);
		thumb.removeEventListener('lostpointercapture', onThumbPointerUp);
		track.removeEventListener('click', onTrackClick);
	};
};

// A fixed-position header does not trigger the same native edge scrolling as the
// normal table header. During a column drag, scroll its table ourselves when the
// pointer is near either visible edge. Leave column reordering to the table library.
const attachColumnDragScrolling = (tableScrollElement, header, scheduleSync) => {
	let isDraggingColumn = false;
	// Keep the latest pointer position so scrolling can continue while it stays still.
	// dragFrame is null when no scrolling frame is waiting to run.
	let dragPosition = null;
	let dragFrame = null;

	const stopScrolling = () => {
		// Forget the last position and cancel pending movement. This does not end the
		// column drag itself: returning to an edge can start scrolling again.
		dragPosition = null;
		if (dragFrame !== null) window.cancelAnimationFrame(dragFrame);
		dragFrame = null;
	};

	const scrollAtEdge = () => {
		dragFrame = null;
		// Only supplement floating-header drags; the normal header keeps native scrolling.
		if (!isDraggingColumn || !dragPosition || header.style.position !== 'fixed') return;
		const headerRect = header.getBoundingClientRect();
		const containerRect = tableScrollElement.getBoundingClientRect();
		// Use the scroll container's inside edges, not the full, partly hidden header.
		// Clamp them to the window so an edge outside the screen is not a scroll target.
		const containerLeft = containerRect.left + tableScrollElement.clientLeft;
		const left = Math.max(0, containerLeft);
		const right = Math.min(window.innerWidth, containerLeft + tableScrollElement.clientWidth);
		const { clientX, clientY } = dragPosition;
		// Pointer coordinates and these rectangles all use positions within the window.
		// Stop if the pointer is above, below or outside the visible header area.
		if (clientY < headerRect.top || clientY > headerRect.bottom || clientX < left || clientX > right) return;
		// On narrow tables, shrink the edge zones so the left and right zones cannot overlap.
		const edgeWidth = Math.min(COLUMN_DRAG_EDGE_WIDTH, (right - left) / 2);
		if (edgeWidth <= 0) return;
		// -1 scrolls left, 1 scrolls right, and the middle of the header does not scroll.
		const direction = clientX < left + edgeWidth ? -1 : clientX > right - edgeWidth ? 1 : 0;
		if (!direction) return;
		// Movement ramps from almost zero at the zone's inner boundary to the maximum
		// at the outer edge. Halfway through the zone gives half the maximum step.
		const distance = direction < 0 ? clientX - left : right - clientX;
		const step = direction * COLUMN_DRAG_SCROLL_STEP * (1 - distance / edgeWidth);
		const previousScrollLeft = tableScrollElement.scrollLeft;
		const maxScrollLeft = Math.max(0, tableScrollElement.scrollWidth - tableScrollElement.clientWidth);
		// Stay within the table's scroll range and stop requesting frames at either end.
		tableScrollElement.scrollLeft = Math.max(0, Math.min(previousScrollLeft + step, maxScrollLeft));
		if (tableScrollElement.scrollLeft === previousScrollLeft) return;
		// Keep the header and thumb aligned, then continue scrolling next frame even
		// without another dragover event. Each frame checks the current bounds again.
		scheduleSync();
		dragFrame = window.requestAnimationFrame(scrollAtEdge);
	};

	const onDragStart = event => {
		// The drag may start on a button inside a heading, rather than on the th itself.
		isDraggingColumn = Boolean(event.target.closest('th'));
	};
	const onDragOver = event => {
		if (!isDraggingColumn) return;
		// Update the position without starting a second animation loop if one is running.
		dragPosition = { clientX: event.clientX, clientY: event.clientY };
		if (dragFrame === null) dragFrame = window.requestAnimationFrame(scrollAtEdge);
	};
	const onDragLeave = event => {
		// relatedTarget is the element being entered. Moving between header children
		// should not stop scrolling; leaving the header (or window) should.
		if (!header.contains(event.relatedTarget)) stopScrolling();
	};
	const onDragEnd = () => {
		// Dropping, cancelling or losing window focus ends this drag's scrolling entirely.
		isDraggingColumn = false;
		stopScrolling();
	};

	header.addEventListener('dragstart', onDragStart);
	header.addEventListener('dragleave', onDragLeave);
	// Capture-phase window listeners see movement and drag completion even outside
	// the header or if a child stops bubbling. Do not cancel the table library's events.
	window.addEventListener('dragover', onDragOver, true);
	window.addEventListener('drop', onDragEnd, true);
	window.addEventListener('dragend', onDragEnd, true);
	window.addEventListener('blur', onDragEnd);

	return () => {
		// Stop any queued scrolling before detaching the listeners when the hook cleans up.
		onDragEnd();
		header.removeEventListener('dragstart', onDragStart);
		header.removeEventListener('dragleave', onDragLeave);
		window.removeEventListener('dragover', onDragOver, true);
		window.removeEventListener('drop', onDragEnd, true);
		window.removeEventListener('dragend', onDragEnd, true);
		window.removeEventListener('blur', onDragEnd);
	};
};

// Keep the real header visible near the top of the screen while the page scrolls.
// We move the original header instead of copying it, so its React buttons, sorting,
// tooltips and column-dragging handlers keep working. This helper owns the header's
// measurements and styles; the hook below decides when to ask it for an update.
const createFloatingHeader = (table, tableScrollElement, header) => {
	// A fixed-position header no longer takes up space in the table's normal layout.
	// Without a replacement, the rows below it would move up and columns could change width.
	// This empty header section will hold that space while the real header floats.
	const spacer = document.createElement('thead');
	// The spacer has no labels or controls. Hide it from screen readers and pointer actions.
	// display: none means it takes no space yet; we enable it only when the real header floats.
	spacer.setAttribute('aria-hidden', 'true');
	Object.assign(spacer.style, { visibility: 'hidden', pointerEvents: 'none', display: 'none' });
	header.after(spacer);
	// Save only the inline styles we are about to change. Restoring them later lets the
	// normal table styling take over again without disturbing unrelated styles.
	const headerProperties = ['position', 'top', 'left', 'width', 'display', 'tableLayout', 'clipPath'];
	const originalHeaderStyles = Object.fromEntries(headerProperties.map(property => [property, header.style[property]]));
	// Each measured column stores its cell, width and original styles together.
	// Keep these measurements between scroll events instead of measuring every time.
	let measuredColumns = [];
	let headerHeight = 0;
	let tableWidth = 0;
	let needsMeasurement = true;

	const restoreHeaderToTable = () => {
		// Put the real header back into the table layout and remove its forced column widths.
		// The spacer is no longer needed, and the saved column measurements are discarded.
		Object.assign(header.style, originalHeaderStyles);
		measuredColumns.forEach(({ cell, originalStyles }) => Object.assign(cell.style, originalStyles));
		measuredColumns = [];
		spacer.style.display = 'none';
	};

	const measureHeaderAndBuildSpacer = () => {
		// Scrolling normally changes positions, not widths. While the header is floating,
		// reuse its sizes until a change such as resizing or hiding a column makes them stale.
		if (!needsMeasurement) return;

		// First let the browser lay out the real header with the table again. Measuring
		// while it still has our fixed widths would only give us the old, forced sizes.
		// getBoundingClientRect() tells us the size and position of an element onscreen.
		restoreHeaderToTable();
		headerHeight = header.getBoundingClientRect().height;
		tableWidth = table.getBoundingClientRect().width;
		const spacerRow = document.createElement('tr');
		measuredColumns = Array.from(header.querySelectorAll('th'), cell => {
			const width = `${cell.getBoundingClientRect().width}px`;
			const originalStyles = {
				width: cell.style.width,
				minWidth: cell.style.minWidth,
				maxWidth: cell.style.maxWidth,
				boxSizing: cell.style.boxSizing
			};
			// Make one empty spacer cell for each heading, using its measured width.
			// Its height holds the header's space. No padding or border should add extra size;
			// border-box means the given size includes any padding and border.
			const spacerCell = document.createElement('td');
			Object.assign(spacerCell.style, {
				width,
				minWidth: width,
				maxWidth: width,
				height: `${headerHeight}px`,
				padding: '0',
				border: '0',
				boxSizing: 'border-box'
			});
			spacerRow.appendChild(spacerCell);
			return { cell, width, originalStyles };
		});
		// Replace the previous spacer row so it matches the current visible column order.
		spacer.replaceChildren(spacerRow);
		needsMeasurement = false;
	};

	// Once fixed to the screen, the header needs our help to follow sideways scrolling.
	// Move it left by the table's scroll amount and hide the parts outside the visible table.
	const floatHeader = (headerOffset, tableContainerRect) => {
		const scrollLeft = tableScrollElement.scrollLeft;
		// Whatever is beyond the visible right edge must be hidden. scrollLeft is the
		// amount hidden on the left; subtracting both leaves the amount hidden on the right.
		const rightClip = Math.max(0, tableWidth - scrollLeft - tableScrollElement.clientWidth);
		Object.assign(header.style, {
			position: 'fixed',
			top: `${headerOffset}px`,
			// Start at the container's inside left edge, then follow the table's sideways scroll.
			// clientLeft accounts for the container's left border.
			left: `${tableContainerRect.left + tableScrollElement.clientLeft - scrollLeft}px`,
			width: `${tableWidth}px`,
			// Keep a table layout with controlled widths even though the header now floats separately.
			display: 'table',
			tableLayout: 'fixed',
			// inset uses top, right, bottom, left amounts. Only the sides need clipping here.
			clipPath: `inset(0 ${rightClip}px 0 ${Math.max(0, scrollLeft)}px)`
		});
		// Force each heading to keep the width it had inside the table. Otherwise its text
		// could make it wider or narrower than the product values underneath it.
		measuredColumns.forEach(({ cell, width }) => {
			Object.assign(cell.style, { width, minWidth: width, maxWidth: width, boxSizing: 'border-box' });
		});
		// The real header now floats, so enable the invisible spacer to hold its old place.
		spacer.style.display = 'table-header-group';
	};

	const updatePosition = (tableRect, tableContainerRect) => {
		// matchMedia checks the screen width against the same mobile cutoff used by the UI.
		// Desktop needs space for navigation plus the top scrollbar. On mobile the top
		// scrollbar is hidden, so the header only needs to sit below the navigation.
		const headerOffset = window.matchMedia(`(max-width: ${TABLE_MOBILE_BREAKPOINT}px)`).matches
			? TABLE_NAVIGATION_HEIGHT
			: FLOATING_TABLE_HEADER_OFFSET;
		// Start floating when the table reaches that screen position. Stop before the
		// floating header would extend past the bottom of the table. Before or after
		// that range, the header belongs in its normal place inside the table.
		const shouldFloat =
			headerHeight > 0 && tableRect.top <= headerOffset && tableRect.bottom > headerOffset + headerHeight;

		if (shouldFloat) {
			floatHeader(headerOffset, tableContainerRect);
		} else {
			restoreHeaderToTable();
			// Normal table layout can choose new widths, so measure again on the next update.
			needsMeasurement = true;
		}
	};

	const invalidateMeasurements = () => {
		// Mark the old sizes as no longer reliable. The scheduled update will measure them,
		// so several changes happening together do not each need their own measurement.
		needsMeasurement = true;
	};

	const destroy = () => {
		// Leave the table as we found it, including removing the extra header section we created.
		restoreHeaderToTable();
		spacer.remove();
	};

	return { measureHeaderAndBuildSpacer, updatePosition, invalidateMeasurements, destroy };
};

// The top scrollbar is sticky through CSS, but it also needs to stop at the table's end.
// As the bottom of the table passes the scrollbar's usual bottom edge, move the bar up
// by the same amount. Once the table ends above the navigation's bottom edge, hide it.
// Scrolling back reverses this: the movement becomes zero and visibility is restored.
const updateScrollbarAtTableBottom = (container, tableBottom, originalVisibility) => {
	container.style.transform = `translateY(${Math.min(0, tableBottom - FLOATING_TABLE_HEADER_OFFSET)}px)`;
	container.style.visibility = tableBottom <= TABLE_NAVIGATION_HEIGHT ? 'hidden' : originalVisibility;
};

// This is the React hook used by ProductsTable. The refs point to elements React rendered.
// The effect connects those elements to the helpers above, listens for scroll and layout
// changes, and cleans everything up when the component goes away or the effect reruns.
const useProductsTableScroll = (tableScrollRef, scrollBarRef, scrollThumbRef) => {
	useEffect(() => {
		// The effect runs after rendering, when ref.current can point to real HTML elements.
		// Find the actual table inside MUI's wrapper rather than scrolling the outer wrapper.
		const tableScrollWrapper = tableScrollRef.current;
		const table = tableScrollWrapper?.querySelector("table[role='grid']");
		// MUI puts the table directly inside the element that scrolls sideways.
		const tableScrollElement = table?.parentElement;
		const header = table?.querySelector('thead');
		const track = scrollBarRef.current;
		const thumb = scrollThumbRef.current;

		// Without all of these elements, there is nothing we can safely connect or position.
		if (!table || !tableScrollElement || !header || !track || !thumb) return undefined;

		// In ProductsTable's markup, the track sits directly inside the sticky scrollbar container.
		// Save its styles so our temporary movement and hiding can be undone during cleanup.
		const scrollBarContainer = track.parentElement;
		const originalScrollBarStyles = {
			transform: scrollBarContainer.style.transform,
			visibility: scrollBarContainer.style.visibility
		};
		const floatingHeader = createFloatingHeader(table, tableScrollElement, header);
		// null means no update is waiting. Otherwise this holds the browser's scheduled-frame ID.
		let scrollFrame = null;

		const syncScrollLayout = () => {
			// This is the shared update: measure if needed, read current positions, then make
			// the scrollbar, header and thumb match. Clear the ID so another update can be requested.
			scrollFrame = null;
			floatingHeader.measureHeaderAndBuildSpacer();
			const tableRect = table.getBoundingClientRect();
			const tableContainerRect = tableScrollElement.getBoundingClientRect();
			updateScrollbarAtTableBottom(scrollBarContainer, tableRect.bottom, originalScrollBarStyles.visibility);
			floatingHeader.updatePosition(tableRect, tableContainerRect);
			updateScrollThumb(tableScrollElement, track, thumb);
		};

		// Scrolling can send many events very quickly. requestAnimationFrame asks the browser
		// to run our update before its next screen redraw. If one is already waiting, do not
		// queue another: that update will read the latest scroll position when it runs.
		const scheduleSync = () => {
			if (scrollFrame === null) scrollFrame = window.requestAnimationFrame(syncScrollLayout);
		};

		// Scrolling mostly changes where the table is onscreen. Resizing or changing columns
		// can also change its sizes, so mark the measurements as stale before scheduling work.
		const handleLayoutChange = () => {
			floatingHeader.invalidateMeasurements();
			scheduleSync();
		};
		// Watch header content and column-layout attributes, but not style or class changes.
		// Our own header updates only change styles, and the spacer sits outside the header.
		// Neither triggers this observer, so it can keep watching while we update the layout.
		const headerObserver = new MutationObserver(handleLayoutChange);
		headerObserver.observe(header, {
			subtree: true,
			childList: true,
			characterData: true,
			attributeFilter: ['colspan', 'rowspan', 'hidden', 'data-colindex']
		});
		// A ResizeObserver reports when an element's size changes. Watching the table and its
		// containers catches changes such as opening the filters panel, even without a window resize.
		const resizeObserver = new ResizeObserver(handleLayoutChange);
		[tableScrollWrapper, tableScrollElement, table, header, track].forEach(element => resizeObserver.observe(element));

		// The table's scroll event handles sideways movement; the window's handles page scrolling.
		// passive tells the browser these handlers will not try to stop normal scrolling.
		tableScrollElement.addEventListener('scroll', scheduleSync, { passive: true });
		window.addEventListener('scroll', scheduleSync, { passive: true });
		window.addEventListener('resize', handleLayoutChange);
		const detachScrollbarControls = attachScrollbarControls(tableScrollElement, track, thumb);
		const detachColumnDragScrolling = attachColumnDragScrolling(tableScrollElement, header, scheduleSync);

		// The first scheduled update sets both the thumb and header positions, even before scrolling.
		scheduleSync();

		// React calls this cleanup when the effect ends. Restore the original HTML/styles,
		// remove our event handlers and observers, and cancel any update still waiting to run.
		// This prevents old handlers or scheduled work from touching a table that is no longer active.
		return () => {
			headerObserver.disconnect();
			resizeObserver.disconnect();
			floatingHeader.destroy();
			Object.assign(scrollBarContainer.style, originalScrollBarStyles);
			tableScrollElement.removeEventListener('scroll', scheduleSync);
			window.removeEventListener('scroll', scheduleSync);
			window.removeEventListener('resize', handleLayoutChange);
			if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame);
			detachScrollbarControls();
			detachColumnDragScrolling();
		};
	}, [scrollBarRef, scrollThumbRef, tableScrollRef]);

	// ProductsTable also calls this returned function from the column picker to refresh
	// the thumb. Full header measurements are handled separately by the observers above.
	return tableScrollElement => updateScrollThumb(tableScrollElement, scrollBarRef.current, scrollThumbRef.current);
};

export default useProductsTableScroll;
